import { useMemo, useRef } from 'react';
import DocumentTitle from 'react-document-title';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import CardLoader from 'hb-react/frontoffice/apps/Forum/components/CardLoader';
import CardPinnedLoader from 'hb-react/frontoffice/apps/Forum/components/CardPinnedLoader';
import {
  MOST_POPULAR,
  SORT_BY,
} from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { useGetForumHeaderProps } from 'hb-react/frontoffice/apps/Forum/hooks/useGetForumHeaderProps';
import { useWhichTemplate } from 'hb-react/frontoffice/apps/Forum/hooks/useWhichTemplate';
import { getTransformCategories } from 'hb-react/frontoffice/apps/Forum/requests';
import {
  makeFetchItems,
  makeFetchPinnedItems,
  makeFetchWidgets,
} from 'hb-react/frontoffice/apps/Forum/utils/fetchItemsUtils';
import {
  makeFilters,
  makeItemRenderer,
  makePinnedRenderer,
  makeWidgetRenderer,
} from 'hb-react/frontoffice/apps/Forum/utils/rendererUtils';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import oneColumnTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/OneColumnTemplate';
import twoColumnsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/TwoColumnsTemplate';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

const ForumIndex = () => {
  const { configUrls, appPageConfig, topicName } = useForumContext();
  const { topicId } = useTopicContext();
  const breakpoint = useBreakpoint();
  const listItemsRef = useRef();
  const template = useWhichTemplate();
  const fetchItems = makeFetchItems({ configUrls });
  const fetchPinnedItems = makeFetchPinnedItems({ configUrls });
  const fetchWidgets = makeFetchWidgets({ configUrls });
  const { currentNetwork } = useDefaultAppContexts();
  const forumMenuName = usePageMenuName(PAGES.FORUM, __('Forum'));

  const appHeaderProps = useGetForumHeaderProps({
    appPageConfig,
    configUrls,
  });

  const filters = useMemo(makeFilters, []);

  const sortByDefault =
    appPageConfig?.config?.default_filter === MOST_POPULAR
      ? SORT_BY[1]
      : SORT_BY[0];

  const itemRenderer = makeItemRenderer(configUrls);
  const pinnedRenderer = makePinnedRenderer();
  const widgetRenderer = makeWidgetRenderer();

  const { controls } = appHeaderProps;

  const topBlock = topicId
    ? {
        ...BLOCKS.headerControls,
        attributes: {
          controls,
        },
        templateAttributes: {
          [twoColumnsTemplate.id]: {
            forceAlign: 'left',
          },
        },
      }
    : {
        ...BLOCKS.appHeader,
        attributes: {
          ...appHeaderProps,
        },
      };

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: forumMenuName,
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName,
        })}
      />
      <BlocksRenderer
        templateId={template.id}
        templateComponent={template.component}
        templateZones={template.zones}
        templateProps={{
          topicId,
          hasFilters: filters?.length > 0,
        }}
        blocks={[
          topBlock,
          {
            ...BLOCKS.filters,
            attributes: {
              filters,
              dataTestId: 'block-filters',
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                filtersByRow: 3,
                filterRows: 1,
              },
              [twoColumnsTemplate.id]: {
                filtersByRow: breakpoint === 'm' ? 3 : 1,
                filterRows: breakpoint === 'm' ? 1 : 3,
                actionButtonsCanOverflow: true,
              },
            },
          },
          {
            ...BLOCKS.slider,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.forum.pinnedItems,
              fetchItems: fetchPinnedItems,
              itemLoader: CardPinnedLoader,
              itemRenderer: pinnedRenderer,
              title: ({ itemsSize, isLoading }) => (
                <>
                  {s__('Forum|Highlighted discussions')}{' '}
                  {!isLoading && `(${itemsSize})`}
                </>
              ),
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                perPageBreakpoints: { s: 1, m: 2, l: 3, xl: 4 },
              },
              [twoColumnsTemplate.id]: {
                perPageBreakpoints: { s: 1, m: 2, l: 2, xl: 3 },
              },
            },
          },
          {
            ...BLOCKS.tags,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.forum.tags,
              multi: true,
              fetchTags: () => getTransformCategories(configUrls),
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                limit: 10,
              },
              [twoColumnsTemplate.id]: {
                label: s__('Forum|Filter by category'),
                limit: 15,
              },
            },
          },
          {
            id: ITEMS_QUERY_KEYS.forum.items,
            ref: listItemsRef,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems,
              itemLoader: CardLoader,
              itemRenderer,
              itemsByRow: 1,
              loaderLines: 1,
              sortBy: SORT_BY,
              initialSort: sortByDefault,
              width: '192px',
            },
          },
          {
            ...BLOCKS.side,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.forum.side,
              fetchItems: fetchWidgets,
              itemLoader: CardLoader,
              itemRenderer: widgetRenderer,
            },
          },
        ]}
      />
    </>
  );
};

export default ForumIndex;
